<template>
    <Modal :show="modalOpen" :closable="!isLoading" @close="close" custom_dialog_css="max-width: 600px;">
        <div slot="header">
            <div class="modal-header">
                <h5 class="modal-title">{{ $t('ceremony.edit_ceremony', {type: ceremony.translated_type}) }}</h5>
                <button
                    type="button"
                    class="close"
                    @click="close"
                    aria-label="Close"
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
        </div>

        <div slot="body">
            <CeremonyForm v-model="formData" :ceremony="ceremony" :disabled="isLoading" :errors="formErrors" />
        </div>

        <div slot="footer">
            <button type="button" :disabled="isLoading" class="btn btn-primary btn-icon btn-sm" @click="updateCeremony">
                {{ $t('general.save') }}
            </button>

            <button type="button" :disabled="isLoading" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="close">
                {{ $t('general.close') }}
            </button>
        </div>
    </Modal>
</template>

<script setup>
    import Modal from '../../components/Modal.vue';
    import { ref, watch } from 'vue';
    import { ipcCall, IpcCall } from '../utils/ipcCall';
    import CeremonyForm from '../../components/CeremonyForm.vue';
    import { STATE } from '../../enums/state';
    import { notifyError, notifySuccess } from '../../utils/notify';
    import i18n from '../../utils/locale';

    const emit = defineEmits(['close', 'updated']);
    const props = defineProps({
        ceremony: {
            type: Object,
            required: true,
        },
    });

    const isLoading = ref(false);
    const modalOpen = ref(true);

    const formErrors = ref(null);
    const formData = ref(buildLocalValue(props.ceremony));

    watch(() => props.ceremony, () => {
        formData.value = buildLocalValue(props.ceremony);
    });

    function buildLocalValue(ceremony) {
        return {
            name: ceremony?.name || '',
            type: ceremony?.type || 'ceremony',
            date_unknown: ceremony?.date_unknown || false,
            date: ceremony?.date || '',
            duration: ceremony?.duration || null,
            location_id: ceremony?.location_id || null,
            state_id: ceremony?.state_id || STATE.OPEN,
            attendees: [
                ceremony?.attendee ? {
                    name: ceremony.attendee.name || '',
                    email: ceremony.attendee.email || '',
                } : {
                    name: '',
                    email: '',
                },
            ],
        };
    }

    async function updateCeremony() {
        isLoading.value = true;
        formErrors.value = null;

        try {
            const ceremony = await ipcCall(IpcCall.UpdateCeremony, {
                ceremonyId: props.ceremony.id,
                data: formData.value,
            });

            emit('updated', ceremony);
            notifySuccess(i18n.t('ceremony.ceremony_updated', { type: props.ceremony.translated_type }))
        } catch (exception) {
            if (exception.response && exception.response.status === 422) {
                formErrors.value = exception.response.data.errors;
            } else {
                if (!exception.response || (exception.response && exception.response.status !== 401)) {
                    notifyError(exception, i18n.t('ceremony.cant_update_ceremony', { type: props.ceremony.translated_type }));
                }
            }
        } finally {
            isLoading.value = false;
        }
    }

    function close() {
        emit('close');
    }
</script>
